/**
 * AssetVersion Model
 * @class
 * @todo Should you be allowed to remove a top version?
 */
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import Base from '@netx/core/lib/netx-model';

export default class AssetVersion extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {string} */
	get idAttribute() {
		return 'versionId';
	}

	/**
	 * @property {object}  defaults                - The default attribute values
	 * @property {number}  defaults.versionId      - The default id (do not set this in defaults)
	 * @property {number}  defaults.active         - The default active status (number as boolean)
	 * @property {number}  defaults.assetId        - The default asset id version belongs to
	 * @property {string}  defaults.creationDate   - The default creation date (as string)
	 * @property {number}  defaults.fileSize       - The default file size
	 * @property {number}  defaults.fileTypeId     - The default file type id
	 * @property {string}  defaults.fileTypeLabel  - The default file type label
	 * @property {number}  defaults.memoId         - The default memoId (unused as far as I can tell - always comes back 0)
	 * @property {string}  defaults.memoText       - The default memo text
	 * @property {number}  defaults.original       - The default original status (number as boolean)
	 * @property {string}  defaults.previewUrl     - The default preview url
	 * @property {string}  defaults.shortMemoText  - The default short memo text (truncated memo text)
	 * @property {string}  defaults.thumbUrl       - The default thumbnail url
	 * @property {number}  defaults.userId         - The default user id
	 * @property {string}  defaults.userLabel      - The default user label
	 */
	get defaults() {
		return {
			// versionId: 0
			active: 0,
			assetId: 0,
			creationDate: '',
			fileSize: 0,
			fileTypeId: 0,
			fileTypeLabel: '',
			memoId: 0,
			memoText: '',
			original: 0,
			previewUrl: '',
			shortMemoText: '',
			thumbUrl: '',
			userId: 0,
			userLabel: '',
		};
	}

	/**
	 * @property {object} methods                         - Model RPC methods
	 * @property {array}  methods.read                    - Fetch version
	 * @property {array}  methods.remove                  - Destroy version
	 * @property {array}  methods.reactivateAssetVersion  - Reactivate version
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getVersionObjects', [this.get('versionId')]];
			},
			/** @method */
			remove: ['deleteAssetVersion', 'versionId'],
			// Reactivate version
			// Deprecated - use new uuid call `reactivateAssetVersion`
			// reactivateVersion: ['reactivateVersion', 'versionId', 'memoText'],
			/** @method */
			reactivateAssetVersion: [
				'reactivateAssetVersion',
				'versionId',
				'memoText',
			],
		};
	}
	/** @type {object} */
	get parsers() {
		return {
			/**
			 * @function reactivateAssetVersion
			 * @param {boolean} response Response
			 * @param {object} opts Request options
			 */
			reactivateAssetVersion(uuid, opts) {
				this.trackBatchJob(uuid);
				dispatcher.trigger(
					'asset:versions:reactivate:progress',
					this.get('assetId'),
					1,
					i18n.t('i18n.reactivatingVersion'),
				);
				return uuid;
			},
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		this.on('progress', (assetVersion, batchJob, progress) => {
			dispatcher.trigger(
				'asset:versions:reactivate:progress',
				this.get('assetId'),
				progress,
				i18n.t('i18n.reactivatingVersion'),
			);
		});
		this.on('complete', (assetVersion, batchJob, progress) => {
			dispatcher.trigger('asset:versions:reactivate:succeeded', this);
		});
		this.on('error', (assetVersion, error) => {
			dispatcher.trigger('asset:versions:reactivate:error', error, this);
		});
	}

	/**
	 * Unwrap the one-object array returned by `getVersionObjects`
	 * @method
	 * @param {object} attrs Model attributes
	 */
	parse(attrs) {
		return _.isArray(attrs) ? attrs[0] : attrs;
	}

	/**
	 * Cache-busting string for asset version objects: encode the versionID and creation date as query args
	 * @method
	 * @param {string} prefix Prefix for cachbuster string
	 * @return {string}
	 */
	cacheBuster(prefix = '') {
		return (
			prefix +
			'?t=' +
			_.dateFromAsset(this.get('creationDate')).getTime() +
			'&v=' +
			this.get('versionId')
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetVersion';
	}
}
